import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { makeStyles } from "tss-react/mui";
import {
  MiDetailFields,
  MiDetailPageHeader,
  MiList,
  MiProfilePicture,
  MiStackedTypography,
} from "@miplatform/components";
import {
  DATE_TIME_FORMATS,
  INPUT_TYPES,
  LANGUAGES_OPTIONS,
  TIMEZONE_OPTIONS,
} from "@miplatform/constants";
import { useRouter } from "@miplatform/hooks";
import { IAvailabilityLog, IUserResponse } from "@miplatform/types";
import { formatDate, getDate } from "@miplatform/utils";
import { Box, Typography } from "@mui/material";
import { useUser } from "../../../hooks/useUser/useUser";
import { ConfigAccordionHeader } from "../../Config/components/ConfigAccordionHeader/ConfigAccordionHeader";

const groupLogs = (records: IAvailabilityLog[]) => {
  if (records.length === 0) return [];

  return [...records]
    .sort((a, b) => getDate(a.from).diff(getDate(b.from)))
    .reduce((acc: IAvailabilityLog[], next) => {
      const lastGroup = acc[acc.length - 1];

      if (!lastGroup) return [next];

      const sameModified = lastGroup.modifiedOn === next.modifiedOn;
      const isContinuous = getDate(lastGroup.to)
        .add(1, "ms")
        .isSame(getDate(next.from));

      if (sameModified && isContinuous) {
        const mergedGroup = { ...lastGroup, to: next.to };

        return [...acc.slice(0, -1), mergedGroup];
      }

      return [...acc, next];
    }, []);
};

export const ProfileDetails = () => {
  const { params } = useRouter();
  const { id } = params;
  const { classes, theme } = useStyles();

  const {
    loading,
    userDetails,
    availabilityLogs,
    getUser,
    getUserAvailabilityLogs,
  } = useUser();
  const { control, reset } = useForm<Partial<IUserResponse>>({
    mode: "onChange",
    defaultValues: {},
  });

  const groupedLogs = useMemo(
    () => groupLogs(availabilityLogs || []),
    [availabilityLogs]
  );

  const basicInformationFields = [
    {
      label: "First Name",
      name: "firstName",
      formControl: control,
      inputProps: { maxLength: 255 },
      readOnly: true,
      width: "25%",
    },
    {
      label: "Last Name",
      name: "lastName",
      formControl: control,
      inputProps: { maxLength: 255 },
      readOnly: true,
      width: "25%",
    },
    {
      label: "Display Name",
      name: "displayName",
      formControl: control,
      inputProps: { maxLength: 255 },
      readOnly: true,
      width: "25%",
    },
    {
      label: "User Name",
      name: "userName",
      formControl: control,
      inputProps: { maxLength: 255 },
      readOnly: true,
      width: "25%",
    },
  ];

  const adminFields = [
    {
      label: "Roles",
      name: "roles",
      value: userDetails?.roles?.map((role) => role.name).join(", "),
      formControl: control,
      inputProps: { maxLength: 255 },
      readOnly: true,
      width: "100%",
    },
  ];

  const contactInformationFields = [
    {
      label: "Email",
      name: "email",
      formControl: control,
      inputProps: { maxLength: 255 },
      readOnly: true,
      width: "50%",
    },
    {
      label: "Phone Number",
      name: "primaryPhoneNumber",
      formControl: control,
      inputProps: { maxLength: 255 },
      readOnly: true,
      width: "50%",
    },
  ];

  const preferencesFields = [
    {
      label: "Language",
      name: "language",
      formControl: control,
      fieldType: INPUT_TYPES.SELECT,
      options: LANGUAGES_OPTIONS,
      readOnly: true,
      width: "50%",
    },
    {
      label: "Time Zone",
      name: "timezone",
      formControl: control,
      fieldType: INPUT_TYPES.SELECT,
      options: TIMEZONE_OPTIONS,
      readOnly: true,
      width: "50%",
    },
  ];

  const cardSections = [
    {
      fieldsetStyle: {
        justifyContent: "flex-end",
        alignItems: "center",
      },
      fieldset: [
        {
          field: "from",
          maxWidth: 400,
          justifyContent: "flex-end",
          renderCell: ({ from, to }: IAvailabilityLog) => (
            <MiStackedTypography
              title="Start Date"
              description={formatDate(
                from,
                DATE_TIME_FORMATS.FORMAT_MMM_D_YYYY_H_MM_A
              )}
              descriptionProps={{
                fontWeight: theme.fontWeight.medium,
              }}
            />
          ),
        },
        {
          field: "to",
          maxWidth: 400,
          justifyContent: "flex-end",
          renderCell: ({ from, to }: IAvailabilityLog) => (
            <MiStackedTypography
              title="End Date"
              description={formatDate(
                to,
                DATE_TIME_FORMATS.FORMAT_MMM_D_YYYY_H_MM_A
              )}
              descriptionProps={{
                fontWeight: theme.fontWeight.medium,
              }}
            />
          ),
        },
      ],
    },
  ];

  useEffect(() => {
    if (id) {
      getUser(id);
      getUserAvailabilityLogs({
        filters: {
          userId: { eq: { type: "guid", value: id } },
          to: { ge: getDate().startOf("day").toDate() },
        },
      });
    }
  }, [id]);

  useEffect(() => {
    reset(userDetails);
  }, [userDetails]);

  return (
    <Box className={classes.root}>
      <MiDetailPageHeader
        title={userDetails?.displayName || "--"}
        loading={loading}
      />
      <Box className={classes.container}>
        <ConfigAccordionHeader title="Profile Picture">
          <Box height={80}>
            <MiProfilePicture
              name={userDetails?.displayName || "--"}
              sx={{
                width: 80,
                height: 80,
                backgroundColor: theme.palette.primary.main,
              }}
            />
          </Box>
        </ConfigAccordionHeader>
        <ConfigAccordionHeader title="Basic Information">
          <Box pt={2}>
            <MiDetailFields detailFields={basicInformationFields} />
          </Box>
        </ConfigAccordionHeader>
        <ConfigAccordionHeader title="Admin">
          <Box pt={2}>
            <MiDetailFields detailFields={adminFields} />
          </Box>
        </ConfigAccordionHeader>
        <ConfigAccordionHeader title="Contact Information">
          <Box pt={2}>
            <MiDetailFields detailFields={contactInformationFields} />
          </Box>
        </ConfigAccordionHeader>
        <ConfigAccordionHeader title="Preferences">
          <Box pt={2}>
            <MiDetailFields detailFields={preferencesFields} />
          </Box>
        </ConfigAccordionHeader>
        <ConfigAccordionHeader title="Unavailable Dates">
          <MiList
            hideListPagination
            viewType="card"
            cardProps={{
              sections: cardSections,
              renderTitle: (log: IAvailabilityLog) => (
                <Box display="flex" flexDirection="column">
                  <Typography
                    noWrap
                    color="primary.main"
                    fontWeight={theme.fontWeight.semiBold}
                    fontSize={14}
                    maxWidth={300}
                  >
                    {log.note || "--"}
                  </Typography>
                  <Box display="flex" alignItems="center">
                    <Typography
                      fontSize={theme.fontSize.small}
                      fontWeight={theme.fontWeight.medium}
                    >
                      Modified:{" "}
                      <Typography
                        component="span"
                        fontSize={theme.fontSize.small}
                      >
                        {formatDate(
                          log.modifiedOn,
                          DATE_TIME_FORMATS.FORMAT_MMMM_D_YYYY
                        )}
                      </Typography>
                    </Typography>
                  </Box>
                </Box>
              ),
            }}
            data={groupedLogs}
          />
        </ConfigAccordionHeader>
      </Box>
    </Box>
  );
};

const useStyles = makeStyles()((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    height: "inherit",
  },
  container: {
    padding: theme.spacing(3),
    gap: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
  },
}));
